import { makeAutoObservable, runInAction } from 'mobx';
import dashboardTranslationsStore from '../dashboard-translation/stores/dashboard-translations-store';
import UISettingsService, { IUISettings, CUSTOMER_COLUMN_USE, RETURNS_DATE_FIELD } from '../services/api/ui-settings-service';
import { TYPE, rootContainer } from '../services/container/root';
import { ISaveable } from './types';

interface IUISettingsChanges extends Partial<IUISettings> { }

export interface IReturnsDateFieldOption {
    id: RETURNS_DATE_FIELD;
    label: string;
}

const RETURNS_TABLE_SETTINGS_SCOPE = 'dashboard-returns-table-settings';

class UISettingsStore implements ISaveable {
    private _model: IUISettings = {} as IUISettings;
    private _changes: IUISettingsChanges = {};
    private _isLoading: boolean = false;
    private _isLoaded: boolean = false;
    private _isSaving: boolean = false;

    constructor() {
        makeAutoObservable(this, {}, {autoBind: true});
    }

    private _setModel(uiSettings: IUISettings) {
        this._model = {
            ...uiSettings,
            manage_returns_new_enabled: uiSettings.manage_returns_new_enabled ?? false,
            manage_returns_promo_declined: uiSettings.manage_returns_promo_declined ?? false,
        };
    }

    private get uiSettingsService(): UISettingsService {
        return rootContainer.get<UISettingsService>(TYPE.UISettingsService);
    }

    public get isLoading(): boolean {
        return this._isLoading;
    }

    /** Stays false until the first load resolves, so nothing is rendered from an empty model. */
    public get isLoaded(): boolean {
        return this._isLoaded;
    }

    public get returnsCustomerDisplayMode(): CUSTOMER_COLUMN_USE {
        return this._changes.returns_customer_display_mode ?? this._model.returns_customer_display_mode;
    }

    public get returnsDateField(): RETURNS_DATE_FIELD {
        return this._changes.returns_date_field ?? this._model.returns_date_field;
    }

    /** Created / Shipped / Delivered choices for the returns date column settings. */
    public get returnsDateFieldOptions(): IReturnsDateFieldOption[] {
        return [
            {
                id: RETURNS_DATE_FIELD.CREATED,
                label: dashboardTranslationsStore.getTranslation('RMA_CREATED', RETURNS_TABLE_SETTINGS_SCOPE),
            },
            {
                id: RETURNS_DATE_FIELD.SHIPPED,
                label: dashboardTranslationsStore.getTranslation('SHIPMENT_IN_TRANSIT', RETURNS_TABLE_SETTINGS_SCOPE),
            },
            {
                id: RETURNS_DATE_FIELD.DELIVERED,
                label: dashboardTranslationsStore.getTranslation('SHIPMENT_DELIVERED', RETURNS_TABLE_SETTINGS_SCOPE),
            },
        ];
    }

    public get manageReturnsNewEnabled(): boolean {
        return this._changes.manage_returns_new_enabled ?? this._model.manage_returns_new_enabled ?? false;
    }

    public get manageReturnsPromoDeclined(): boolean {
        return this._changes.manage_returns_promo_declined ?? this._model.manage_returns_promo_declined ?? false;
    }

    public setReturnsCustomerDisplayMode(value: CUSTOMER_COLUMN_USE) {
        this._changes.returns_customer_display_mode = value;
    }

    public setReturnsDateField(value: RETURNS_DATE_FIELD) {
        this._changes.returns_date_field = value;
    }

    public hasChanges() {
        return !!Object.keys(this._changes).length;
    }

    async load() {
        this._isLoading = true;
        try {
            const uiSettings = await this.uiSettingsService.fetchUISettings();
            runInAction(() => {
                this._isLoading = false;
                this._isLoaded = true;
                this._setModel(uiSettings);
                this._changes = {};
            });
        } catch (error) {
            console.error(error);
            runInAction(() => {
                this._isLoading = false;
            });
        }
    }

    async save() {
        if (!this.hasChanges()) {
            return;
        }
        this._isSaving = true;
        try {
            /** Only the edited fields are sent, so saving table settings never touches the redesign state. */
            const updatedUISettings = await this.uiSettingsService.saveUISettings(this._changes);
            runInAction(() => {
                this._isSaving = false;
                this._setModel(updatedUISettings);
                this._changes = {};
            });
        } catch (error) {
            console.error(error);
            runInAction(() => {
                this._isSaving = false;
            });
        }
    }

    /** Switching the design also retires the promo - the user has clearly seen it by now. */
    async saveManageReturnsNewEnabled(value: boolean): Promise<boolean> {
        return this.saveManageReturns({
            manage_returns_new_enabled: value,
            manage_returns_promo_declined: true,
        });
    }

    async saveManageReturnsPromoDeclined(): Promise<boolean> {
        if (this.manageReturnsPromoDeclined) {
            return true;
        }

        return this.saveManageReturns({ manage_returns_promo_declined: true });
    }

    private async saveManageReturns(changes: IUISettingsChanges): Promise<boolean> {
        this._isSaving = true;
        try {
            const updatedUISettings = await this.uiSettingsService.saveUISettings(changes);
            runInAction(() => {
                this._isSaving = false;
                this._setModel(updatedUISettings);
            });
            return true;
        } catch (error) {
            console.error(error);
            runInAction(() => {
                this._isSaving = false;
            });
            return false;
        }
    }

    public isSaving(): boolean {
        return this._isSaving;
    }
}

export default new UISettingsStore();
